import { Loader, Paper, Stack, Text } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS, runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import { buildNavigatePath } from '@/routes.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { useAgentTraining } from '@/store/agent-training';
import { AgentCreationStep } from '@/types/agent-training.ts';
import { cn } from '@/utils';

const TOTAL_DURATION_MS = 3 * 60 * 1000;

const AgentSetupProgress = () => {
	const steps = useMemo(() => {
		return [
			'analyzing_business_data',
			'understanding_services_offers',
			'learning_customer_needs',
			'extracting_workflows',
			'building_conversation_logic',
			'adapting_business_rules',
			'training_intent_recognition',
			'optimizing_responses',
			'running_quality_checks',
			'finalizing_ai_agent'
		];
	}, []);
	const { isMiniCreatorMode, localization, handleAgentCreationSuccess } =
		useHostContext();
	const navigate = useNavigate();
	const {
		agentSetupProgressStep,
		incrementAgentProgressStep,
		setAgentProgressSteps,
		setAgentStep
	} = useAgentTraining();

	const stepDuration = useMemo(
		() => Math.floor(TOTAL_DURATION_MS / steps.length),
		[steps.length]
	);

	const { start, clear } = useTimeout(() => {
		incrementAgentProgressStep(steps.length - 1);
	}, stepDuration);

	useEffect(() => {
		if (agentSetupProgressStep >= steps.length - 1) {
			clear();
			return;
		}
		start();
		return clear;
	}, [agentSetupProgressStep, steps.length, start, clear]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run only one time
	useEffect(() => {
		const offOnboardingFinished = agentTrainingRealtime.onOnboardingFinished(
			() => {
				setAgentProgressSteps(steps.length - 1);

				setTimeout(() => {
					handleAgentCreationSuccess?.();
					if (runtimeConfig.isRemoteBuild) {
						setAgentStep(AgentCreationStep.SUCCESS);
						return;
					}
					navigate(buildNavigatePath(PATHS.agentSuccess));
				}, 5000);
			}
		);

		return () => {
			offOnboardingFinished();
		};
	}, []);

	return (
		<Paper
			className={cn('p-6 h-full ', {
				'!rounded-[20px] sm:!rounded-[40px]': !isMiniCreatorMode,
				'p-2': isMiniCreatorMode
			})}
		>
			{isMiniCreatorMode ? (
				<div className="relative h-full overflow-hidden">
					<AnimatePresence mode="wait">
						<motion.div
							animate={{ opacity: 1, y: 0 }}
							className="flex items-center gap-3 h-full"
							exit={{ opacity: 0, y: -10 }}
							initial={{ opacity: 0, y: 10 }}
							key={agentSetupProgressStep}
							transition={{ type: 'spring', stiffness: 260, damping: 22 }}
						>
							<Loader size={16} />
							<Text
								className="!text-primary-900"
								fw={600}
								size="sm"
							>
								{localization.translate(
									'creator',
									`agent_setup_steps.${steps[agentSetupProgressStep]}`
								)}
							</Text>
						</motion.div>
					</AnimatePresence>
				</div>
			) : (
				<Stack gap={30}>
					{steps.map((step, index) => {
						const isCompleted = index < agentSetupProgressStep;
						const isActive = index === agentSetupProgressStep;

						return (
							<div
								className="flex items-start gap-4"
								key={step}
							>
								{index <= agentSetupProgressStep ? (
									<div className="relative w-[24px] h-[24px] flex-shrink-0">
										<AnimatePresence mode="wait">
											{isActive && !isCompleted && (
												<motion.div
													animate={{ opacity: 1, scale: 1 }}
													className="absolute inset-0 flex items-center justify-center"
													exit={{ opacity: 0, scale: 0.6 }}
													initial={{ opacity: 0, scale: 0.6 }}
													key="loader"
												>
													<Loader size={16} />
												</motion.div>
											)}

											{!isActive && (
												<motion.div
													animate={{ scale: 1, opacity: 1 }}
													className="absolute inset-0"
													initial={{ scale: 0.6, opacity: 0 }}
													key="checkbox"
													transition={{
														type: 'spring',
														stiffness: 420,
														damping: 26
													}}
												>
													<motion.div
														className={cn(
															'absolute inset-0 rounded-md border-2 #868e96',
															{
																'!border !border-primary-900 !bg-primary-900':
																	isCompleted
															}
														)}
														transition={{ duration: 0.2 }}
													/>

													<AnimatePresence>
														{isCompleted && (
															<motion.div
																animate={{ scale: 1 }}
																className="absolute inset-0 flex items-center justify-center"
																initial={{ scale: 0.7 }}
																transition={{
																	type: 'spring',
																	stiffness: 420,
																	damping: 22
																}}
															>
																<motion.svg
																	aria-hidden="true"
																	fill="none"
																	focusable="false"
																	height="10"
																	viewBox="0 0 14 10"
																	width="14"
																>
																	<motion.path
																		animate={{ pathLength: 1 }}
																		d="M1.5 5.5L5.5 9L12.5 1.5"
																		initial={{ pathLength: 0 }}
																		stroke="white"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth="2.8"
																		transition={{ duration: 0.28 }}
																	/>
																</motion.svg>
															</motion.div>
														)}
													</AnimatePresence>
												</motion.div>
											)}
										</AnimatePresence>
									</div>
								) : (
									<span className="w-[24px] h-[24px]" />
								)}

								<Text
									c={isCompleted ? 'dimmed' : 'blue.9'}
									className="!text-primary-900"
									fw={isActive ? 600 : 400}
									opacity={index > agentSetupProgressStep ? 0.5 : 1}
									size="sm"
								>
									{localization.translate(
										'creator',
										`agent_setup_steps.${step}`
									)}
								</Text>
							</div>
						);
					})}
				</Stack>
			)}
		</Paper>
	);
};

export default AgentSetupProgress;
